import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChevronDown, 
  Menu, 
  X, 
  LogOut, 
  BookOpen, 
  Briefcase, 
  Users, 
  Stethoscope,
  Code,
  Wrench,
  Utensils,
  Building2,
  Truck,
  Home,
  FileText,
  Heart,
  GraduationCap,
  MessageSquare,
  PenTool,
  Library,
  Car
} from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const closeDropdowns = () => {
    setActiveDropdown(null);
  };

  const grammarLevels = [
    { level: 'A1', name: 'Beginner', color: 'bg-green-500' },
    { level: 'A2', name: 'Elementary', color: 'bg-teal-500' },
    { level: 'B1', name: 'Intermediate', color: 'bg-blue-500' },
    { level: 'B2', name: 'Upper Intermediate', color: 'bg-blue-500' },
    { level: 'C1', name: 'Advanced', color: 'bg-blue-500' },
    { level: 'C2', name: 'Mastery', color: 'bg-pink-500' }
  ];

  const careerPaths = [
    { icon: <Stethoscope className="w-4 h-4" />, name: 'Healthcare & Nursing', path: 'healthcare', color: 'text-red-500' },
    { icon: <Code className="w-4 h-4" />, name: 'IT & Technology', path: 'it', color: 'text-blue-500' },
    { icon: <Wrench className="w-4 h-4" />, name: 'Engineering & Trades', path: 'engineering', color: 'text-orange-500' },
    { icon: <Utensils className="w-4 h-4" />, name: 'Hospitality & Gastronomy', path: 'hospitality', color: 'text-amber-500' },
    { icon: <Building2 className="w-4 h-4" />, name: 'Business & Office', path: 'business', color: 'text-blue-500' },
    { icon: <Truck className="w-4 h-4" />, name: 'Logistics & Transport', path: 'logistics', color: 'text-emerald-500' }
  ];

  const integrationModules = [
    { icon: <FileText className="w-4 h-4" />, name: 'Visa & Immigration', path: 'visa' },
    { icon: <Home className="w-4 h-4" />, name: 'Finding Housing', path: 'housing' },
    { icon: <Heart className="w-4 h-4" />, name: 'Healthcare System', path: 'healthcare-system' },
    { icon: <GraduationCap className="w-4 h-4" />, name: 'Education & Training', path: 'education' },
    { icon: <Briefcase className="w-4 h-4" />, name: 'Opening a Business', path: 'business-setup' },
    { icon: <Car className="w-4 h-4" />, name: "Driver's License", path: 'fuhrerschein' },
    { icon: <Users className="w-4 h-4" />, name: 'Daily Life & Culture', path: 'daily-life' }
  ];

  const practiceModules = [
    { icon: <Library className="w-4 h-4" />, name: 'Vocabulary', path: '/vocabulary' },
    { icon: <MessageSquare className="w-4 h-4" />, name: 'Reading', path: '/reading' },
    { icon: <MessageSquare className="w-4 h-4" />, name: 'Convos', path: '/convos' }
  ];

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-babyblue-200 sticky top-0 z-50 shadow-sm" data-testid="main-navbar">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link" onClick={closeDropdowns}>
            <span className="font-heading text-2xl font-extrabold text-primary hover:text-primary-hover transition-all">Germanatic</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 relative z-50">
            
            {/* Grammar Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('grammar')}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeDropdown === 'grammar' 
                    ? 'bg-babyblue-100 text-primary' 
                    : 'text-babyblue-700 hover:bg-babyblue-50 hover:text-primary'
                }`}
                data-testid="nav-grammar-dropdown"
              >
                <BookOpen className="w-4 h-4" />
                Grammar
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'grammar' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'grammar' && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-babyblue-200 rounded-xl shadow-xl py-2 min-w-[220px] z-50">
                  <div className="px-3 py-2 border-b border-babyblue-100">
                    <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider">CEFR Levels</p>
                  </div>
                  {grammarLevels.map((item) => (
                    <Link
                      key={item.level}
                      to={`/grammar?level=${item.level}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-babyblue-700 hover:bg-babyblue-50 hover:text-primary transition-colors"
                      onClick={closeDropdowns}
                    >
                      <span className={`w-2 h-2 rounded-full ${item.color}`}></span>
                      <span className="font-medium">{item.level}</span>
                      <span className="text-babyblue-500 text-sm">- {item.name}</span>
                    </Link>
                  ))}
                  <div className="border-t border-babyblue-100 mt-2 pt-2">
                    <Link
                      to="/grammar"
                      className="flex items-center gap-3 px-4 py-2.5 text-primary font-medium hover:bg-babyblue-50 transition-colors"
                      onClick={closeDropdowns}
                    >
                      View All Grammar →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Career Paths Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('careers')}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeDropdown === 'careers' 
                    ? 'bg-babyblue-100 text-primary' 
                    : 'text-babyblue-700 hover:bg-babyblue-50 hover:text-primary'
                }`}
                data-testid="nav-careers-dropdown"
              >
                <Briefcase className="w-4 h-4" />
                Career Paths
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'careers' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'careers' && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-babyblue-200 rounded-xl shadow-xl py-2 min-w-[260px] z-50">
                  <div className="px-3 py-2 border-b border-babyblue-100">
                    <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider">Professional German</p>
                  </div>
                  {careerPaths.map((item) => (
                    <Link
                      key={item.path}
                      to={`/careers/${item.path}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-babyblue-700 hover:bg-babyblue-50 hover:text-primary transition-colors"
                      onClick={closeDropdowns}
                    >
                      <span className={item.color}>{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Integration Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('integration')}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeDropdown === 'integration' 
                    ? 'bg-babyblue-100 text-primary' 
                    : 'text-babyblue-700 hover:bg-babyblue-50 hover:text-primary'
                }`}
                data-testid="nav-integration-dropdown"
              >
                <Users className="w-4 h-4" />
                Integration
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'integration' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'integration' && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-babyblue-200 rounded-xl shadow-xl py-2 min-w-[240px] z-50">
                  <div className="px-3 py-2 border-b border-babyblue-100">
                    <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider">Life in Germany</p>
                  </div>
                  {integrationModules.map((item) => (
                    <Link
                      key={item.path}
                      to={`/integration/${item.path}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-babyblue-700 hover:bg-babyblue-50 hover:text-primary transition-colors"
                      onClick={closeDropdowns}
                    >
                      <span className="text-primary">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Practice Dropdown */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('practice')}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeDropdown === 'practice' 
                    ? 'bg-babyblue-100 text-primary' 
                    : 'text-babyblue-700 hover:bg-babyblue-50 hover:text-primary'
                }`}
                data-testid="nav-practice-dropdown"
              >
                <PenTool className="w-4 h-4" />
                Practice
                <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === 'practice' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'practice' && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-babyblue-200 rounded-xl shadow-xl py-2 min-w-[200px] z-50">
                  <div className="px-3 py-2 border-b border-babyblue-100">
                    <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider">Exercises</p>
                  </div>
                  {practiceModules.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="flex items-center gap-3 px-4 py-2.5 text-babyblue-700 hover:bg-babyblue-50 hover:text-primary transition-colors"
                      onClick={closeDropdowns}
                    >
                      <span className="text-primary">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Auth UI hidden — Germanatic is fully free, no login required */}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-babyblue-700 p-2"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-babyblue-200 pt-4 max-h-[80vh] overflow-y-auto">
            <div className="space-y-4">
              
              {/* Grammar Section */}
              <div>
                <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider px-2 mb-2">Grammar Levels</p>
                <div className="grid grid-cols-3 gap-2">
                  {grammarLevels.map((item) => (
                    <Link
                      key={item.level}
                      to={`/grammar?level=${item.level}`}
                      className="flex items-center justify-center gap-1 p-2 bg-babyblue-50 rounded-lg text-babyblue-700 hover:bg-babyblue-100 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className={`w-2 h-2 rounded-full ${item.color}`}></span>
                      <span className="font-medium">{item.level}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Career Paths */}
              <div>
                <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider px-2 mb-2">Career Paths</p>
                <div className="space-y-1">
                  {careerPaths.map((item) => (
                    <Link
                      key={item.path}
                      to={`/careers/${item.path}`}
                      className="flex items-center gap-3 px-3 py-2 text-babyblue-700 hover:bg-babyblue-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className={item.color}>{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Integration */}
              <div>
                <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider px-2 mb-2">Integration</p>
                <div className="space-y-1">
                  {integrationModules.map((item) => (
                    <Link
                      key={item.path}
                      to={`/integration/${item.path}`}
                      className="flex items-center gap-3 px-3 py-2 text-babyblue-700 hover:bg-babyblue-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="text-primary">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Practice */}
              <div>
                <p className="text-xs font-medium text-babyblue-500 uppercase tracking-wider px-2 mb-2">Practice</p>
                <div className="grid grid-cols-2 gap-2">
                  {practiceModules.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="flex items-center gap-2 px-3 py-2 bg-babyblue-50 rounded-lg text-babyblue-700 hover:bg-babyblue-100 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="text-primary">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Auth UI hidden — Germanatic is fully free, no login required */}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close dropdowns */}
      {activeDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={closeDropdowns}
        ></div>
      )}
    </nav>
  );
};

export default Navbar;
